import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { getDefaultStore, useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useState, type JSX } from "react";

import IllustrationConfetti from "@repo/icons/colored/IllustrationConfetti";
import Download from "@repo/icons/default/Download";
import { CpsToast } from "corpus";

import { RegulatoryEntrancesAlert } from "@/components/regulatory-documents/regulatory-entrances-alert";
import { RegulatoryRequestSummary } from "@/components/regulatory-documents/regulatory-request-summary";
import { Button } from "@/components/ui/button/button";
import { Page } from "@/components/ui/layout/page/page";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { useDownloadFiles } from "@/hooks/use-download-files";
import { regulatoryDocumentAtom } from "@/lib/atoms/regulatory-document-atom";
import { regulatoryDocumentResultAtom } from "@/lib/atoms/regulatory-document-result-atom";
import { trackEvent } from "@/lib/tracking";

const RegulatoryDocumentDownloadPage = (): JSX.Element => {
  const navigate = useNavigate();
  const draft = useAtomValue(regulatoryDocumentAtom);
  const requests = useAtomValue(regulatoryDocumentResultAtom);
  const resetDraft = useResetAtom(regulatoryDocumentAtom);
  const resetRequests = useResetAtom(regulatoryDocumentResultAtom);

  const [isDownloading, setIsDownloading] = useState(false);
  const [openErrorToast, setOpenErrorToast] = useState(false);

  const { handleDownload } = useDownloadFiles(
    requests.map((request) => request.urlDownload),
    () => {
      setOpenErrorToast(true);
    },
    (index) => `${requests[index]?.nomeArquivo ?? "documento"}.pdf`,
  );

  const missingFilesCount = draft.selectedLocations.length - requests.length;

  const downloadFiles = async (): Promise<void> => {
    setIsDownloading(true);
    try {
      const succeeded = await handleDownload();
      if (succeeded) {
        trackEvent("Documento Regulatório Baixado", {
          codTipoDocumentoRegulatorio: draft.codTipoDocumentoRegulatorio,
          quantidadeArquivos: requests.length,
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleBackToDocuments = async (): Promise<void> => {
    await navigate({ to: "/settings/regulatory-documents", replace: true });
    resetDraft();
    resetRequests();
  };

  return (
    <RoundedLayout>
      <Page
        className="flex h-full flex-col justify-between gap-5 px-6 pb-0 pt-10"
        showCustomerIoContainer={false}
      >
        <div className="flex flex-col gap-5">
          <div className="flex flex-col items-center gap-2 text-center">
            <IllustrationConfetti size={80} aria-hidden="true" />
            <h1 className="text-xl font-bold text-neutral-700">Documento disponível</h1>
            <p className="text-sm text-neutral-500">
              {requests.length > 1
                ? "Seus PDFs estão prontos para download"
                : "Seu PDF está pronto para download"}
            </p>
            {missingFilesCount > 0 ? (
              <p className="text-xs text-danger-600">
                {missingFilesCount === 1
                  ? "1 unidade não retornou arquivo. Tente novamente mais tarde."
                  : `${missingFilesCount} unidades não retornaram arquivo. Tente novamente mais tarde.`}
              </p>
            ) : null}
          </div>

          <RegulatoryRequestSummary draft={draft} requests={requests} />

          {draft.temEntradasDiferentes ? <RegulatoryEntrancesAlert /> : null}

          <Button
            fullWidth
            Icon={Download}
            loading={isDownloading}
            onClick={() => void downloadFiles()}
          >
            Baixar PDF
          </Button>
        </div>

        <div className="sticky bottom-0 flex w-full flex-col bg-white pb-6 pt-4">
          <Button
            fullWidth
            color="secondaryLight"
            onClick={() => void handleBackToDocuments()}
          >
            Voltar para Documentos
          </Button>
        </div>
      </Page>

      <CpsToast
        title="Ocorreu um erro ao baixar o arquivo. Espere alguns minutos e tente novamente. Caso o erro persista, entre em contato com nosso suporte."
        show={openErrorToast}
        onClose={() => {
          setOpenErrorToast(false);
        }}
        type="danger"
        duration={10}
      />
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/settings/regulatory-documents/download")({
  component: RegulatoryDocumentDownloadPage,
  beforeLoad: ({ context }) => {
    if (!context.flags["exibe-documentos-regulatorios-no-app"]) {
      redirect({ to: "/settings", throw: true });
    }

    const requests = getDefaultStore().get(regulatoryDocumentResultAtom);
    if (requests.length === 0) {
      redirect({ to: "/settings/regulatory-documents", throw: true });
    }
  },
});
